import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0;
`;

const AddBtn = styled.button`
  padding: 10px 20px;
  background: #2DBCAF;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover { background: #25a99d; }
`;

const Table = styled.div`
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px 120px 80px 100px 120px;
  gap: 12px;
  padding: 16px 20px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;

  @media (max-width: 900px) {
    display: none;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px 120px 80px 100px 120px;
  gap: 12px;
  padding: 16px 20px;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
  &:hover { background: #f8fafc; }

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
`;

const VoucherCode = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #2DBCAF;
  font-family: monospace;
`;

const Badge = styled.span<{ active?: boolean }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  background: ${p => p.active ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  color: ${p => p.active ? '#16a34a' : '#dc2626'};
`;

const ActionBtns = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionBtn = styled.button<{ danger?: boolean }>`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: ${p => p.danger ? 'rgba(239, 68, 68, 0.1)' : 'rgba(100, 116, 139, 0.1)'};
  color: ${p => p.danger ? '#dc2626' : '#64748b'};
  &:hover { opacity: 0.8; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

/* Modal */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 20px;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1a1f2e;
  margin: 0 0 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  &:focus { outline: none; border-color: #2DBCAF; }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  &:focus { outline: none; border-color: #2DBCAF; }
`;

const CheckboxGroup = styled.div`
  display: flex;
  gap: 16px;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #475569;
  cursor: pointer;
`;

const ModalBtns = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const ModalBtn = styled.button<{ primary?: boolean }>`
  flex: 1;
  padding: 12px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: ${p => p.primary ? 'none' : '1px solid #e2e8f0'};
  background: ${p => p.primary ? '#2DBCAF' : 'white'};
  color: ${p => p.primary ? 'white' : '#64748b'};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

interface Voucher {
  _id: string;
  code: string;
  type: 'percentage' | 'fixed';
  value: number;
  maxDiscount?: number;
  quota?: number;
  usedCount: number;
  validUntil?: string;
  applicableTo?: string[];
  isActive: boolean;
  createdAt: string;
}

interface FormData {
  code: string;
  type: 'percentage' | 'fixed';
  value: string;
  maxDiscount: string;
  quota: string;
  validUntil: string;
  applicableTo: string[];
  isActive: boolean;
}

const defaultForm: FormData = {
  code: '',
  type: 'percentage',
  value: '',
  maxDiscount: '',
  quota: '',
  validUntil: '',
  applicableTo: [],
  isActive: true,
};

const VouchersPage: React.FC = () => {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(defaultForm);
  const [saving, setSaving] = useState(false);

  const fetchVouchers = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/vouchers', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setVouchers(data.vouchers);
      }
    } catch (err) {
      console.error('Failed to fetch vouchers:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  const openCreate = () => {
    setEditingId(null);
    setForm(defaultForm);
    setShowModal(true);
  };

  const openEdit = (v: Voucher) => {
    setEditingId(v._id);
    setForm({
      code: v.code,
      type: v.type,
      value: v.value.toString(),
      maxDiscount: v.maxDiscount?.toString() || '',
      quota: v.quota?.toString() || '',
      validUntil: v.validUntil ? v.validUntil.split('T')[0] : '',
      applicableTo: v.applicableTo || [],
      isActive: v.isActive,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.code || !form.value) return;
    setSaving(true);

    try {
      const body = {
        code: form.code.toUpperCase(),
        type: form.type,
        value: Number(form.value),
        maxDiscount: form.maxDiscount ? Number(form.maxDiscount) : null,
        quota: form.quota ? Number(form.quota) : null,
        validUntil: form.validUntil || null,
        applicableTo: form.applicableTo.length > 0 ? form.applicableTo : null,
        isActive: form.isActive,
      };

      const url = editingId ? `/api/admin/vouchers/${editingId}` : '/api/admin/vouchers';
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (res.ok) {
        fetchVouchers();
        setShowModal(false);
      } else {
        const data = await res.json();
        alert(data.error || 'Failed to save voucher');
      }
    } catch {
      alert('Failed to save voucher');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, code: string) => {
    if (!confirm(`Hapus voucher "${code}"?`)) return;

    try {
      const res = await fetch(`/api/admin/vouchers/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        setVouchers(vouchers.filter(v => v._id !== id));
      }
    } catch {
      alert('Failed to delete voucher');
    }
  };

  const toggleApplicable = (pkg: string) => {
    setForm(f => ({
      ...f,
      applicableTo: f.applicableTo.includes(pkg)
        ? f.applicableTo.filter(p => p !== pkg)
        : [...f.applicableTo, pkg],
    }));
  };

  const formatValue = (v: Voucher) => {
    if (v.type === 'percentage') return `${v.value}%`;
    return `Rp ${v.value.toLocaleString('id-ID')}`;
  };

  const formatQuota = (v: Voucher) => {
    if (!v.quota) return '∞';
    return `${v.usedCount}/${v.quota}`;
  };

  return (
    <>
      <PageHeader>
        <Title>🎟️ Voucher</Title>
        <AddBtn onClick={openCreate}>+ Tambah Voucher</AddBtn>
      </PageHeader>

      <Table>
        <TableHeader>
          <div>Kode</div>
          <div>Tipe</div>
          <div>Nilai</div>
          <div>Kuota</div>
          <div>Status</div>
          <div>Aksi</div>
        </TableHeader>

        {loading ? (
          <EmptyState>Loading...</EmptyState>
        ) : vouchers.length === 0 ? (
          <EmptyState>Belum ada voucher</EmptyState>
        ) : (
          vouchers.map(v => (
            <TableRow key={v._id}>
              <VoucherCode>{v.code}</VoucherCode>
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                {v.type === 'percentage' ? 'Persen' : 'Nominal'}
              </div>
              <div style={{ fontSize: '14px', fontWeight: 600 }}>
                {formatValue(v)}
              </div>
              <div style={{ fontSize: '13px', color: '#64748b' }}>
                {formatQuota(v)}
              </div>
              <div>
                <Badge active={v.isActive}>
                  {v.isActive ? 'Aktif' : 'Nonaktif'}
                </Badge>
              </div>
              <ActionBtns>
                <ActionBtn onClick={() => openEdit(v)}>Edit</ActionBtn>
                <ActionBtn danger onClick={() => handleDelete(v._id, v.code)}>Hapus</ActionBtn>
              </ActionBtns>
            </TableRow>
          ))
        )}
      </Table>

      {/* Create/Edit Modal */}
      {showModal && (
        <ModalOverlay onClick={() => setShowModal(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalTitle>{editingId ? 'Edit Voucher' : 'Tambah Voucher'}</ModalTitle>
            
            <FormGroup>
              <Label>Kode Voucher *</Label>
              <Input
                type="text"
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="HEMAT50"
                disabled={!!editingId}
              />
            </FormGroup>

            <FormGroup>
              <Label>Tipe Diskon *</Label>
              <Select value={form.type} onChange={e => setForm({ ...form, type: e.target.value as any })}>
                <option value="percentage">Persentase (%)</option>
                <option value="fixed">Nominal (Rp)</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Nilai {form.type === 'percentage' ? '(%)' : '(Rp)'} *</Label>
              <Input
                type="number"
                value={form.value}
                onChange={e => setForm({ ...form, value: e.target.value })}
                placeholder={form.type === 'percentage' ? '50' : '10000'}
              />
            </FormGroup>

            {form.type === 'percentage' && (
              <FormGroup>
                <Label>Maksimal Potongan (Rp)</Label>
                <Input
                  type="number"
                  value={form.maxDiscount}
                  onChange={e => setForm({ ...form, maxDiscount: e.target.value })}
                  placeholder="10000"
                />
              </FormGroup>
            )}

            <FormGroup>
              <Label>Kuota (kosongkan untuk unlimited)</Label>
              <Input
                type="number"
                value={form.quota}
                onChange={e => setForm({ ...form, quota: e.target.value })}
                placeholder="100"
              />
            </FormGroup>

            <FormGroup>
              <Label>Berlaku Sampai</Label>
              <Input
                type="date"
                value={form.validUntil}
                onChange={e => setForm({ ...form, validUntil: e.target.value })}
              />
            </FormGroup>

            <FormGroup>
              <Label>Berlaku untuk Paket</Label>
              <CheckboxGroup>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={form.applicableTo.includes('monthly')}
                    onChange={() => toggleApplicable('monthly')}
                  />
                  Bulanan
                </CheckboxLabel>
                <CheckboxLabel>
                  <input
                    type="checkbox"
                    checked={form.applicableTo.includes('yearly')}
                    onChange={() => toggleApplicable('yearly')}
                  />
                  Tahunan
                </CheckboxLabel>
              </CheckboxGroup>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: 4 }}>
                Kosongkan = berlaku untuk semua
              </div>
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={e => setForm({ ...form, isActive: e.target.checked })}
                />
                Voucher Aktif
              </CheckboxLabel>
            </FormGroup>

            <ModalBtns>
              <ModalBtn onClick={() => setShowModal(false)}>Batal</ModalBtn>
              <ModalBtn primary onClick={handleSave} disabled={saving || !form.code || !form.value}>
                {saving ? 'Menyimpan...' : 'Simpan'}
              </ModalBtn>
            </ModalBtns>
          </ModalBox>
        </ModalOverlay>
      )}
    </>
  );
};

export default VouchersPage;
